import React, { useMemo } from "react";
import classnames from "classnames";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { AlertNotification, ResultSensorData, SensorData } from "@shared/models";
import { SENSOR_SIGNAL_TYPE } from "@shared/constants";
import { Loader } from "@shared/components";

import { ArtifactLogicDot, CustomTooltip } from "./components/";

import "./index.scss";

export interface SensorsChartProps {
  lineStroke: string;
  loading?: boolean;
  sensorsSignalType: SENSOR_SIGNAL_TYPE;
  sensorsChartData: SensorData[] | ResultSensorData[];
  sensorsChartNotifications?: AlertNotification[];
  xAxisInterval?: number;
  domain?: [number, number];
  ticks?: number[];
  referenceLineYValue?: number | null;
  isArtifact: boolean;
  onTickFormatter: (value: number) => string;
  onDotClick?: (dot: any) => void;
  onGetSensorChartData: (sensorData: (SensorData | ResultSensorData)[], sensorsSignalType: SENSOR_SIGNAL_TYPE) => any[];
}

const SensorsChart: React.FC<SensorsChartProps> = (props) => {
  const {
    lineStroke,
    loading,
    sensorsChartData,
    sensorsSignalType,
    domain,
    referenceLineYValue,
    onTickFormatter,
    onDotClick,
    onGetSensorChartData,
  } = props;

  const sensorDataSeries = useMemo(() => {
    return onGetSensorChartData(sensorsChartData, sensorsSignalType);
  }, [sensorsChartData, onGetSensorChartData, sensorsSignalType]);

  const yDomain: [number, number] = useMemo(() => {
    const allValues = sensorDataSeries.flatMap((series) =>
      series.data
        .map((d: { value: number | null }) => d.value)
        .filter((v: number | null): v is number => v !== null && v !== undefined),
    );

    if (!allValues.length) {
      return [0, 100];
    }

    const maxValue = Math.max(...allValues);
    const buffer = referenceLineYValue || Math.ceil(maxValue * 0.1) || 10;

    return [0, Math.ceil(maxValue + buffer)];
  }, [sensorDataSeries, referenceLineYValue]);

  return (
    <div
      className={classnames("sensor-chart-container", {
        "sensor-chart-container_no-sensors": !sensorDataSeries.length,
      })}
    >
      {loading ? <Loader className="chart-loader" /> : null}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart width={500} height={300} margin={{ top: 5, right: 5, bottom: 5, left: -28 }}>
          <CartesianGrid stroke="#E3EBF0" />

          <XAxis domain={domain} dataKey="time" type="number" tickFormatter={onTickFormatter} />
          <YAxis dataKey="value" yAxisId="value" stroke="#869CAC" domain={yDomain} />
          {referenceLineYValue ? <ReferenceLine yAxisId="value" y={referenceLineYValue} stroke="#3491e3" /> : null}
          <Tooltip labelFormatter={onTickFormatter} content={<CustomTooltip tickFormatter={onTickFormatter} />} />
          {sensorDataSeries.map((item) => (
            <Line
              stroke={lineStroke}
              dataKey="value"
              yAxisId="value"
              isAnimationActive={false}
              type="monotone"
              data={item.data}
              name={item.param}
              connectNulls={false}
              key={`value-${item.id}`}
              dot={<ArtifactLogicDot adaptiveVisibility />}
              activeDot={{
                className: "active-dot",
                onClick: (_, dotInfo) => onDotClick?.(dotInfo),
                r: 5,
              }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SensorsChart;
